import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { XeroStatusError } from "@/components/admin/XeroStatusStates";
import type {
  XeroChangeLogFieldChange,
  XeroChangeLogPage,
  XeroChangeLogParams,
  XeroChangeLogSource,
} from "@/services/xeroApi";

const ANY = "__any__";
const PAGE_SIZE = 50;
const COLUMN_COUNT = 7;

const SOURCE_LABELS: Record<XeroChangeLogSource, string> = {
  edit: "Edited",
  bulk_import: "Import from Excel",
  cascade: "Cascaded",
  seed: "Seeded from database",
};

function formatAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : format(date, "dd/MM/yyyy HH:mm");
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function formatValue(
  value: XeroChangeLogFieldChange["old"],
  isDate = false,
): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === "") return "(blank)";
  // A "YYYY-MM-DD" date is reordered as text, never through Date, which would
  // read it as UTC midnight and show the previous day west of Greenwich.
  const iso = isDate && typeof value === "string" ? ISO_DATE.exec(value) : null;
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : String(value);
}

export default function XeroChangeLogView({
  title,
  labelHeading,
  fieldLabels,
  dateFields = [],
  sources,
  queryKey,
  fetchPage,
  backTo,
  backLabel,
}: {
  title: string;
  /** Heading for the row-name column, e.g. "Organisation". */
  labelHeading: string;
  fieldLabels: Record<string, string>;
  /** Fields whose "YYYY-MM-DD" values are shown as DD/MM/YYYY. */
  dateFields?: readonly string[];
  /** The sources this table can record, in the order the filter lists them. */
  sources: readonly XeroChangeLogSource[];
  queryKey: readonly unknown[];
  fetchPage: (params: XeroChangeLogParams) => Promise<XeroChangeLogPage>;
  backTo: string;
  backLabel: string;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [source, setSource] = useState<XeroChangeLogSource | "">("");

  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed === debouncedSearch) return;
    const handle = window.setTimeout(() => setDebouncedSearch(trimmed), 300);
    return () => window.clearTimeout(handle);
  }, [search, debouncedSearch]);

  const logs = useInfiniteQuery({
    queryKey: [...queryKey, { search: debouncedSearch, source }],
    queryFn: ({ pageParam }) =>
      fetchPage({
        limit: PAGE_SIZE,
        cursor: pageParam,
        search: debouncedSearch || undefined,
        source: source || undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    // Keeps the current rows on screen while a new filter loads, instead of
    // flashing back to the loading state on every keystroke.
    placeholderData: keepPreviousData,
  });

  const entries = logs.data?.pages.flatMap((page) => page.entries) ?? [];
  const retentionDays = logs.data?.pages[0]?.retention_days;
  const isFiltered = debouncedSearch !== "" || source !== "";

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setSource("");
  };

  return (
    <div className="space-y-4">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
      >
        <ArrowLeft className="h-3 w-3" />
        {backLabel}
      </Link>

      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
          <p className="text-xs text-muted-foreground">
            Changes made through Prism.
            {retentionDays !== undefined &&
              ` Entries are deleted automatically after ${retentionDays} days.`}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => logs.refetch()}
          disabled={logs.isFetching}
          title="Refresh"
          aria-label={`Refresh ${title.toLowerCase()}`}
        >
          <RefreshCw className={`h-4 w-4 ${logs.isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full space-y-1.5 sm:w-72">
          <Label htmlFor="change-log-search">{labelHeading}</Label>
          <Input
            id="change-log-search"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-full space-y-1.5 sm:w-56">
          <Label htmlFor="change-log-source">Source</Label>
          <Select
            value={source || ANY}
            onValueChange={(v) => setSource(v === ANY ? "" : (v as XeroChangeLogSource))}
          >
            <SelectTrigger id="change-log-source">
              <SelectValue placeholder="Any source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any source</SelectItem>
              {sources.map((s) => (
                <SelectItem key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(isFiltered || search !== "") && (
          <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {logs.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading change log…
        </div>
      ) : logs.isError ? (
        <XeroStatusError
          message={(logs.error as Error)?.message ?? "Couldn’t load the change log."}
          onRetry={() => logs.refetch()}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead className="w-[150px]">Date and time</TableHead>
                  <TableHead className="w-[220px]">Changed by</TableHead>
                  <TableHead className="w-[170px]">Source</TableHead>
                  <TableHead className="w-[240px]">{labelHeading}</TableHead>
                  <TableHead className="w-[180px]">Field</TableHead>
                  <TableHead>Old value</TableHead>
                  <TableHead>New value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className="text-center text-sm text-muted-foreground"
                    >
                      {isFiltered
                        ? "No changes match the current filters."
                        : "No changes recorded yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.flatMap((entry) => {
                    // An entry with no field detail still gets one row, so
                    // who/when/what is never hidden.
                    const changes = entry.changes.length > 0 ? entry.changes : [null];
                    const span = changes.length;
                    return changes.map((change, index) => (
                      <TableRow
                        key={`${entry.id}-${index}`}
                        className={`hover:bg-transparent ${index < span - 1 ? "border-b-0" : ""}`}
                      >
                        {index === 0 && (
                          <>
                            <TableCell
                              rowSpan={span}
                              className="whitespace-nowrap align-top text-xs text-muted-foreground"
                            >
                              {formatAt(entry.at)}
                            </TableCell>
                            <TableCell rowSpan={span} className="align-top text-xs">
                              {entry.by}
                            </TableCell>
                            <TableCell rowSpan={span} className="align-top">
                              <Badge variant="outline" className="whitespace-nowrap">
                                {SOURCE_LABELS[entry.source] ?? entry.source}
                              </Badge>
                            </TableCell>
                            <TableCell rowSpan={span} className="align-top">
                              {entry.label || "(blank)"}
                            </TableCell>
                          </>
                        )}
                        {change ? (
                          <>
                            <TableCell className="text-xs">
                              {fieldLabels[change.field] ?? change.field}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {formatValue(change.old, dateFields.includes(change.field))}
                            </TableCell>
                            <TableCell className="text-xs">
                              {formatValue(change.new, dateFields.includes(change.field))}
                            </TableCell>
                          </>
                        ) : (
                          <TableCell colSpan={3} className="text-xs text-muted-foreground">
                            No field detail recorded
                          </TableCell>
                        )}
                      </TableRow>
                    ));
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {logs.hasNextPage && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => logs.fetchNextPage()}
                disabled={logs.isFetchingNextPage}
              >
                {logs.isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin" />}
                {logs.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
